import { Component, OnInit, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { LocalizationService } from '../../core/localization/localization.service';
import { IoTApiService, IoTDeviceRecord } from './iot-api.service';

@Component({
  standalone: true,
  selector: 'ain-iot-devices',
  imports: [AinPageComponent, AinStateComponent, AinDatePipe, FormsModule, TableModule, ButtonModule, InputTextModule, RouterLink],
  template: `
    <ain-page [title]="t('iot.devices.title')" [description]="t('iot.devices.description')">
      <section class="create-box">
        <h3>{{ t('iot.devices.createTitle') }}</h3>
        <div class="grid">
          <label>
            <span>{{ t('iot.devices.fields.deviceType') }}</span>
            <input pInputText [(ngModel)]="createForm.deviceType" />
          </label>
          <label>
            <span>{{ t('iot.devices.fields.model') }}</span>
            <input pInputText [(ngModel)]="createForm.model" />
          </label>
        </div>
        <button pButton type="button" [label]="t('common.create')" [disabled]="creating()" (click)="create()"></button>
      </section>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.loadFailed')" [message]="error() ?? ''" />
      } @else if (!rows().length) {
        <ain-state type="empty" [title]="t('common.noRecords')" />
      } @else {
        <p-table [value]="rows()" [paginator]="true" [rows]="15" dataKey="id">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('iot.devices.fields.deviceType') }}</th>
              <th>{{ t('iot.devices.fields.model') }}</th>
              <th>{{ t('iot.devices.fields.status') }}</th>
              <th>{{ t('iot.devices.fields.lastSeen') }}</th>
              <th></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ row.deviceType }}</td>
              <td>{{ row.model }}</td>
              <td>{{ row.status }}</td>
              <td>{{ row.lastSeenUtc ? (row.lastSeenUtc | ainDate:'datetime') : t('common.notAvailable') }}</td>
              <td><a [routerLink]="['/iot/devices', row.id]">{{ t('common.open') }}</a></td>
            </tr>
          </ng-template>
        </p-table>
      }
    </ain-page>
  `,
  styles: [`
    .create-box { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; margin-bottom: 16px; background: #f8fafc; }
    .grid { display: grid; gap: 10px; margin-bottom: 10px; }
    label { display: grid; gap: 6px; }
    a { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
  `]
})
export class IoTDevicesComponent implements OnInit {
  private readonly api = inject(IoTApiService);
  private readonly localization = inject(LocalizationService);

  readonly rows = signal<IoTDeviceRecord[]>([]);
  readonly loading = signal(true);
  readonly creating = signal(false);
  readonly error = signal<string | null>(null);

  createForm = {
    deviceType: 'VehicleTelematics',
    model: 'MK-1'
  };

  ngOnInit(): void { this.load(); }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.api.listDevices().subscribe({
      next: rows => { this.rows.set(rows); this.loading.set(false); },
      error: err => { this.error.set(err?.message ?? 'load failed'); this.loading.set(false); }
    });
  }

  create(): void {
    this.creating.set(true);
    this.api.createDevice({
      partyId: null,
      deviceType: this.createForm.deviceType,
      model: this.createForm.model,
      metadataJson: '{}'
    }).subscribe({
      next: () => { this.creating.set(false); this.load(); },
      error: () => this.creating.set(false)
    });
  }

  t(key: string): string { return this.localization.translate(key); }
}
